/* cáene Mugler-preview — R05 our community.
   Ägs av sections/caene-community.liquid. Fristående fil, inga beroenden.

   Elementet definieras bara en gång och connectedCallback river alltid en
   tidigare uppsättning lyssnare först. Det är det som gör omladdning i
   temaeditorn ofarlig: shopify:section:load ger ett nytt element, och ett
   gammalt som rivs tar med sig sin AbortController.

   Utan skriptet finns remsan kvar med native scroll, svep och länkar. Därför
   ligger knapparna dolda i markupen och visas först här, och bara när remsan
   faktiskt går att scrolla. */
if (!customElements.get('caene-community')) {
  customElements.define(
    'caene-community',
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.cancelFrame();
        this.cleanupVideos?.();
        this.controller = new AbortController();
        this.setupVideos(this.controller.signal);

        const track = this.querySelector('.caene-community__track');
        const prev = this.querySelector('.caene-community__nav--prev');
        const next = this.querySelector('.caene-community__nav--next');
        if (!track || !prev || !next) return;

        const cards = Array.from(track.children);
        if (cards.length < 2) return;

        const signal = this.controller.signal;
        const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

        // Steget mäts live i stället för att antas, eftersom kortbredden är
        // en procentsats och mellanrummet byter värde vid 750px.
        const step = () => {
          const card = track.firstElementChild;
          if (!card) return 0;
          const width = card.getBoundingClientRect().width;
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          return width + gap;
        };

        const update = () => {
          const max = track.scrollWidth - track.clientWidth;
          const scrollable = max > 1;
          prev.hidden = !scrollable;
          next.hidden = !scrollable;
          prev.disabled = !scrollable || track.scrollLeft <= 1;
          next.disabled = !scrollable || track.scrollLeft >= max - 1;
        };

        this.schedule = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            // En frame som redan låg i kö när elementet revs får inte röra
            // knappar som inte längre hör till dokumentet.
            if (!this.isConnected) return;
            update();
          });
        };

        const move = (direction) => {
          const distance = step();
          if (!distance) return;
          track.scrollBy({
            left: direction * distance,
            behavior: reduceMotion.matches ? 'auto' : 'smooth',
          });
        };

        prev.addEventListener('click', () => move(-1), { signal });
        next.addEventListener('click', () => move(1), { signal });
        track.addEventListener('scroll', this.schedule, { passive: true, signal });
        window.addEventListener('resize', this.schedule, { signal });

        update();
      }

      disconnectedCallback() {
        this.cleanupVideos?.();
        this.controller?.abort();
        this.cancelFrame();
      }

      setupVideos(signal) {
        const videos = Array.from(this.querySelectorAll('.caene-community__track video'));
        if (!videos.length) return;
        const reduced = matchMedia('(prefers-reduced-motion: reduce)');
        const visible = new Set();
        const buttons = [];
        const dialog = document.createElement('dialog');
        dialog.className = 'caene-community__dialog';
        dialog.setAttribute('aria-label', 'Community video');
        dialog.innerHTML = '<button type="button" class="caene-community__close" aria-label="Close video" autofocus>×</button><video controls playsinline preload="none"></video>';
        this.append(dialog);
        const player = dialog.querySelector('video');
        const sourceUrl = (video) => video.currentSrc || video.getAttribute('src') ||
          video.dataset.src || video.querySelector('source')?.getAttribute('src') ||
          video.querySelector('source')?.dataset.src;
        const restorePoster = (video) => {
          const parked = video.dataset.communityPoster;
          if (parked && !video.getAttribute('poster')) {
            video.setAttribute('poster', parked);
          }
        };
        // Posterbilderna vägde 181 kB och konkurrerade med LCP på varje sida
        // (sektionen ligger i footer-gruppen). De hämtas nu strax innan de syns,
        // med 800 px marginal så ingen tom ruta hinner visas. Mätt 2026-09-21.
        const posterObserver = new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (!isIntersecting) return;
            restorePoster(target);
            posterObserver.unobserve(target);
          });
        }, { rootMargin: '800px 0px', threshold: 0 });
        videos.forEach((video) => posterObserver.observe(video));
        if (signal) signal.addEventListener('abort', () => posterObserver.disconnect(), { once: true });

        const hydrate = (video) => {
          restorePoster(video);
          let changed = false;
          if (video.dataset.src && !video.getAttribute('src')) {
            video.src = video.dataset.src;
            changed = true;
          }
          video.querySelectorAll('source[data-src]').forEach((source) => {
            if (source.getAttribute('src')) return;
            source.src = source.dataset.src;
            changed = true;
          });
          if (changed) video.load();
        };
        const play = (video) => {
          hydrate(video);
          video.play()?.catch(() => {});
        };
        const sync = () => videos.forEach((video) => {
          video.muted = true;
          if (visible.has(video) && !document.hidden && !dialog.open && !reduced.matches) play(video);
          else video.pause();
        });
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) visible.add(target);
            else visible.delete(target);
          });
          sync();
        }, { threshold: 0.25 });
        videos.forEach((video) => {
          video.muted = true;
          video.loop = true;
          video.controls = false;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'caene-community__open';
          button.setAttribute('aria-label', `Watch with sound: ${video.getAttribute('aria-label') || 'community video'}`);
          button.setAttribute('aria-haspopup', 'dialog');
          video.parentElement.append(button);
          buttons.push(button);
          button.addEventListener('click', () => {
            videos.forEach((preview) => preview.pause());
            player.src = sourceUrl(video);
            player.poster = video.poster || video.dataset.communityPoster || '';
            player.muted = false;
            player.loop = true;
            dialog.showModal();
            play(player);
          }, { signal });
          observer.observe(video);
        });
        dialog.querySelector('button').addEventListener('click', () => dialog.close(), { signal });
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, { signal });
        dialog.addEventListener('close', () => {
          player.pause();
          player.muted = true;
          player.removeAttribute('src');
          player.load();
          sync();
        }, { signal });
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) player.pause();
          sync();
        }, { signal });
        reduced.addEventListener('change', sync, { signal });
        this.cleanupVideos = () => {
          observer.disconnect();
          player.pause();
          player.removeAttribute('src');
          player.load();
          dialog.remove();
          buttons.forEach((button) => button.remove());
          videos.forEach((video) => { video.pause(); video.controls = true; });
        };
      }

      cancelFrame() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
    }
  );
}
